import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { POPUP } from '../../../../../redux/types';

import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import useExcelExportFormat from '../../../../ui/table/hooks/useExcelExportFormat';
import useWidgetUtils from '../../../hooks/useWidgetUtils';
import { getFieldName } from '../../../../../common/utility/getFieldName';

import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { useInternationalization } from '@progress/kendo-react-intl';


import { IBoardObjectNode } from '../../../types/IBoardObjectNode';
import { IBoardMeasure } from '../../../types/IBoardMeasure';
import { IWidgetData } from '../../../types/IWidgetData';
import { IWidgetStaticData } from '../../../types/IWidgetStaticData';
import { IBoardNote } from '../../../types/IBoardNote';
import { WidgetProps } from '../../../types/IWidgetProps';
import { ICurrency } from '../../../types/ICurrency';
import { IGridPreparedData } from './types/IGridPreparedData';
import { IGridDefinitionColumn } from './types/IGridDefinitionColumn';
import { IGridSetup } from './types/IGridSetup';
import { IGridDefinition } from './types/IGridDefinition';
import { GridWidgetGroupingDescriptor } from './types/GridWidgetGroupingDescriptor';

import Icon from '../../../../ui/icon/icon';
import WidgetWrapper from '../widgetWrapper';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';
import MultiPeriodGrid from './multiPeriodGrid/multiPeriodGrid';
import SinglePeriodGrid from './singlePeriodGrid/singlePeriodGrid';
import Tabs from '../../../../ui/tabs/tabs';

import './gridWidget.scss';


const GridWidget: React.FC<WidgetProps> = ({
                                             widget,
                                             selectedObject,
                                             selectedPeriod,
                                             selectedCurrency,
                                             boardData,
                                             scenarios,
                                             notes,
                                             customisation,
                                             content,
                                             moveWidget,
                                             boards,
                                             removeWidget,
                                             handleLink,
                                             homeContent,
                                             widgetView,
                                             handlePinWidget,
                                             handleUnpinWidget,
                                             handleUnpinTrigger,
                                             unpinId,
                                           }) => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [gridData, setGridData] = useState<IGridPreparedData[]>([]);
  const [nodes, setNodes] = useState<IBoardObjectNode[]>([]);
  const [nodeGroups, setNodeGroups] = useState<IBoardObjectNode[]>([]);
  const [gridTabs, setGridTabs] = useState<string[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [gridColumns, setGridColumns] = useState<IGridDefinitionColumn[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [gridSetup, setGridSetup] = useState<IGridSetup>();
  const [gridNotes, setGridNotes] = useState<IBoardNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [objectValueState, setObjectValueState] = useState<Map<number, any> | null>(null);

  const excelFormat = useExcelExportFormat();
  const { defineNodes, generateCurrencies, fetchNodeData, fetchCombinedData } = useBoardContentWidgetData();
  const { openCashflowDialog } = useWidgetUtils('grid', boardData?.measures || [], objectValueState, homeContent);
  const { widgetSetup } = useBoardContentWidget();
  const exportRef = useRef<any>(null);
  const dispatch = useDispatch();

  const intl = useInternationalization();
  const params = useParams();

  const [isHistorical, setIsHistorical] = useState(false);

  const initialFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };

  const initialSortRuleSingle: SortDescriptor[] = [
    {
      field: 'name',
      dir: 'asc'
    },
  ];


  const [filter, setFilter] = useState(initialFilter);
  const [sortRules, setSortRules] = useState<SortDescriptor[]>(initialSortRuleSingle);
  const [grouping, setGrouping] = useState<GridWidgetGroupingDescriptor[]>([]);

  let objectValueMap = new Map<number, any>();

  const measuresForFetch = (setup: IGridSetup) => {
    let measures: IBoardMeasure[] = [];
    setup?.definitions?.forEach((def) => {
      def.columns?.forEach((column) => {
        if (column.measureId && !measures.find((m) => m.id === column.measureId)) {
          measures.push({ id: column.measureId });
        }
      });
    });
    return measures;
  };

  const propertiesForFetch = (setup: IGridSetup) => {
    let properties: any[] = [];
    setup?.definitions?.forEach((def) => {
      def.columns?.forEach((column) => {
        if (column.templatePropertyId && !properties.find((p) => p.templatePropertyId === column.templatePropertyId)) {
          properties.push({ templatePropertyId: column.templatePropertyId });
        }
      });
    });
    return properties;
  };

  const extractGridTabs = (setup: IGridSetup) => {
    let gridTabsArray: string[] = [];

    setup?.definitions?.forEach((definition: IGridDefinition) => {
      if (definition.name) {
        gridTabsArray.push(definition.name);
      }
    });

    setGridTabs(gridTabsArray);
  };

  const generateColumns = (setup: IGridSetup, nodes: IBoardObjectNode[]) => {
    let returnColumns = setup?.definitions && setup?.definitions[selectedTab].columns;
    if (setup.singlePeriod) {
      setGridColumns((setup?.definitions && setup?.definitions[selectedTab].columns) || []);
    } else {
      let columns: IGridDefinitionColumn[] = [];
      let totalColumns: IGridDefinitionColumn[] = [];

      nodes.forEach((node) => {
        setup?.definitions &&
        setup?.definitions[selectedTab].columns?.forEach((column) => {
          const measure = boardData?.measures?.find((m) => m.id === column.measureId);
          const property = boardData?.staticProperties?.find((p) => p.id === column.templatePropertyId);

          if (measure) {
            columns.push({
              ...column,
              nodeId: node.id,
              nodeName: node.name,
              format: measure.isPercent ? 'p' : 'n',
            });

            if (column.total && !totalColumns.find((item) => item.measureId === column.measureId))
              totalColumns.push({ ...column, isTotal: true, format: measure.isPercent ? 'p' : 'n' });
          }

          if (property) {
            columns.push({
              ...column,
              nodeId: node.id,
              nodeName: node.name,
            });
          }
        });
      });

      totalColumns.forEach((totalColumn) => {
        columns.push(totalColumn);
      });

      returnColumns = columns;
      setGridColumns(columns);
    }
    return returnColumns;
  };

  const prepRow = (preparedData: IGridPreparedData[], node: IBoardObjectNode, row: IWidgetData | IWidgetStaticData, field: string, valueField: string) => {
    let objectValue = objectValueMap.get(row['objectId']);

    if (objectValue) {
      objectValue.row[field] = row[valueField];
    } else {
      objectValue = {
        row: {
          objectId: row.objectId,
          name: node.name,
          nodeId: row?.nodeId,
          parentNodeId: row.parentNodeId,
          currency: node.currency,
        },
        map: {},
      };

      objectValue.row[field] = row[valueField];
      objectValueMap.set(row['objectId'], objectValue);
      setObjectValueState(objectValueMap);

      preparedData.push(objectValue.row);
    }

    return objectValue;
  };

  const createDataValueInfo = (objectValue: any, dataRow: IWidgetData, node: IBoardObjectNode, key: number | string) => {
    objectValue.map[key] = {
      nodeId: node.id,
      scenarioId: node.scenarioId,
      valueId: dataRow.id,
    };
  };

  const prepareData = (setup: IGridSetup, measureValues: IWidgetData[], nodes: IBoardObjectNode[], staticValues?: IWidgetStaticData[]): IGridPreparedData[] => {
    let preparedData: IGridPreparedData[] = [];
    objectValueMap.clear();
    setObjectValueState(null);

    measureValues.forEach((row) => {
      let measure = boardData?.measures?.find((m) => m.id === row.measureId);
      if (measure) {
        let node = nodes.find((n) => n.id === row.nodeId);
        if (node) {
          let fields = getFieldNamesForMeasure(measure.id, '', setup);
          fields.forEach((field) => {
            //@ts-ignore
            let valueField = measure.dataType === 'date' ? 'dateValue' : 'amount';
            let objectValue = prepRow(preparedData, node, row, field, valueField);
            if (objectValue)
              objectValue.row[field] = {
                rowAmount:  objectValue.row[field],
                isPercent: measure?.isPercent,
                scale: measure?.scale,
              };
            //@ts-ignore
            createDataValueInfo(objectValue, row, node, measure?.id || 0);
          });
        }
      }
    });

    if (staticValues) {
      staticValues.forEach((staticRow) => {
        let property = boardData?.staticProperties?.find((p) => p.id === staticRow.templatePropertyId);
        if (property) {
          let node = nodes.find((item) => {
            return item.id === staticRow.nodeId;
          });

          if (node) {
            let fields = getFieldNamesForProperty(property.id, '', setup);
            fields.forEach((field) => {
              //@ts-ignore
              let objectValue = prepRow(preparedData, node, staticRow, field, 'rawValue');
              if (objectValue) {
                objectValue.row[field] = {
                  rowAmount: objectValue.row[field],
                  type: staticRow?.type,
                  scale: staticRow?.scale,
                  dataType: staticRow?.dataType,
                };
              }
            });
          }
        } else {
          let node = nodes.find((item) => {
            return item.id === staticRow.nodeId;
          });

          if (node) {
            let fields = getFieldNamesForProperty(staticRow.templatePropertyId, '', setup);
            fields.forEach((field) => {
              //@ts-ignore
              let objectValue = prepRow(preparedData, node, staticRow, field, 'rawValue');
              if (objectValue)
                objectValue.row[field] = {
                  rowAmount: objectValue.row[field],
                  type: staticRow?.type,
                  scale: staticRow?.scale,
                  dataType: staticRow?.dataType,
                };
            });
          }
        }
      });
    }

    const extendedPreparedData = preparedData.map((item) => {
      const extendedItem = { ...item };
      Object.keys(item).forEach((key) => {
        const nestedObject = item[key];
        // @ts-ignore
        if (nestedObject && nestedObject.rowAmount !== undefined) {
          // @ts-ignore
          extendedItem[`${key}_rowAmount`] = nestedObject.rowAmount;
        }
      });

      return extendedItem;
    });

    return extendedPreparedData;

  };

  const prepRowHistorical = (preparedData: IGridPreparedData[], row: any, field: string, valueField: string) => {
    let objectValue = objectValueMap.get(row['periodId']);

    if (objectValue) {
      objectValue.row[field] = row[valueField];
    } else {
      objectValue = {
        row: {
          periodId: row.periodId,
          periodName: row.periodName,
          startDate: row.startDate,
          endDate: row.endDate,
        },
        map: {},
      };

      objectValue.row[field] = row[valueField];

      objectValueMap.set(row['periodId'], objectValue);
      setObjectValueState(objectValueMap);

      preparedData.push(objectValue.row);
    }

    return objectValue;
  };

  const getFieldNameFor = (fieldId, type, id, nodeId) => {
    return 'ID' + fieldId + '_' + type + '_' + id + (nodeId ? '_' + nodeId : '');
  };

  const getFieldNamesForProperty = (propertyId, nodeId, setup: IGridSetup) => {
    let fieldNames: string[] = [];
    setup?.definitions?.forEach((definition) => {
      definition.columns?.forEach((column) => {
        if (column.templatePropertyId === propertyId) fieldNames.push(getFieldNameFor(column.columnId, 'property', propertyId, nodeId));
      });
    });
    return fieldNames;
  };

  const getFieldNamesForMeasure = (measureId, nodeId, setup: IGridSetup) => {
    let fieldNames: string[] = [];
    setup?.definitions?.forEach((definition) => {
      definition.columns?.forEach((column) => {
        if (column.measureId === measureId) fieldNames.push(getFieldNameFor(column.columnId, 'measure', measureId, nodeId));
      });
    });
    return fieldNames;
  };

  const prepareDataHistorical = (
    setup: IGridSetup,
    measureValues: IWidgetData[],
    nodes: IBoardObjectNode[],
    staticValues?: IWidgetStaticData[],
  ): IGridPreparedData[] => {

    let preparedData: IGridPreparedData[] = [];
    objectValueMap.clear();
    setObjectValueState(null);

    measureValues.forEach((row) => {
      let measure = boardData?.measures?.find((m) => m.id === row.measureId);
      if (measure) {
        let valueField = measure.dataType === 'date' ? 'dateValue' : 'amount';
        let node = nodes.find((item) => {
          return item.id === row.nodeId;
        });

        if (node) {
          let fields = getFieldNamesForMeasure(measure.id, node.id, setup);
          fields.forEach((field) => {
            let objectValue = prepRowHistorical(preparedData, row, field, valueField);

            if (objectValue) {
              let key = measure?.id + '_' + node?.id;

              objectValue.map[key] = {
                nodeId: node?.id,
                scenarioId: node?.scenarioId,
                valueId: row.id,
                periodId: row.periodId,
              };

              objectValue.row[field] = {
                rowAmount: objectValue.row[field],
                isPercent: measure?.isPercent,
                scale: measure?.scale,
                dataType: measure?.dataType,
              };

              let totalField = getFieldNameFor('TOTAL', 'measure', measure?.id, '');
              let totalAmt = totalField && objectValue.row[totalField] ? objectValue.row[totalField].rowAmount : 0;

              objectValue.row[totalField] = {
                rowAmount: totalAmt + (objectValue.row[field].rowAmount || 0),
                isPercent: measure?.isPercent,
                scale: measure?.scale,
                dataType: measure?.dataType,
              };
            }
          });
        }
      }
    });

    if (staticValues) {
      staticValues.forEach((staticRow) => {
        let property = boardData?.staticProperties?.find((p) => p.id === staticRow.templatePropertyId);

        if (property) {
          let node = nodes.find((item) => {
            return item.id === staticRow.nodeId;
          });

          if (node) {
            let fields = getFieldNamesForProperty(property.id, node.id, setup);
            fields.forEach((field) => {
              preparedData.forEach((row) => {
                let objectValue = prepRowHistorical(
                  preparedData,
                  {
                    ...staticRow,
                    periodId: row.periodId,
                  },
                  field,
                  'rawValue',
                );

                if (objectValue)
                  objectValue.row[field] = {
                    rowAmount: objectValue.row[field],
                    type: staticRow?.type,
                    scale: staticRow?.scale,
                    dataType: staticRow?.dataType,
                  };
              });
            });
          }
        }
      });
    }

    const extendedPreparedData = preparedData.map((item) => {
      const extendedItem = { ...item };
      Object.keys(item).forEach((key) => {
        const nestedObject = item[key];
        // @ts-ignore
        if (nestedObject && nestedObject.rowAmount !== undefined) {
          // @ts-ignore
          extendedItem[`${key}_rowAmount`] = nestedObject.rowAmount;
        }
      });

      return extendedItem;
    });

    return extendedPreparedData;

  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);

    if (gridSetup && selectedPeriod) {
      setLoading(true);

      const drill = gridSetup.sliceGroup && gridSetup.sliceGroupLevel;

      const prom = drill
        ? fetchNodeData({
          ...widget,
          investorObjectId: widget.investorObjectId || parseInt(params.investorId),
        }, nodes, measuresForFetch(gridSetup), selectedPeriod, e.value.currency || '', false).then((data) => {
          return {
            measureValues: data,
            staticValues: [],
          };
        })
        : fetchCombinedData(
          { ...widget, investorObjectId: widget.investorObjectId || parseInt(params.investorId) },
          nodes,
          nodeGroups,
          measuresForFetch(gridSetup),
          propertiesForFetch(gridSetup),
          selectedPeriod,
          e.value.currency || '',
          false,
        );

      prom
        .then((res) => {
          let data = gridSetup.singlePeriod
            ? prepareData(gridSetup, res?.measureValues, nodes, res?.staticValues)
            : prepareDataHistorical(gridSetup, res?.measureValues, nodes, res?.staticValues);

          //let dataFormattedDate = transformDateRowAmount(data);
          //setGridData(dataFormattedDate);
          setGridData(data);
          setLoading(false);
        })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const filterNotes = (notes: IBoardNote[]) => {
    let gNotes: IBoardNote[] = [];
    const currentColumns = gridSetup?.definitions && gridSetup.definitions[selectedTab].columns;
    for (let i = 0; i < notes.length; i++) {
      const measures = notes[i] && notes[i].measures;
      if (measures) {
        for (let j = 0; j < measures.length; j++) {
          let measureId = measures[j].measureId;
          currentColumns?.forEach((column) => {
            if (column.measureId === measureId && !gNotes.includes(notes[i])) {
              gNotes.push(notes[i]);
            }
          });
        }
      }
    }

    setGridNotes(gNotes);

    if (gNotes.length > 0 && gridSetup?.singlePeriod && currentColumns) extractNotesForGridColumns(gNotes, currentColumns);
  };

  const extractNotesForGridColumns = (notes: IBoardNote[], columns: IGridDefinitionColumn[]) => {
    setGridColumns(
      columns.map((column) => {
        let itemNotes: IBoardNote[] = [];
        const measureId = column.measureId;
        notes.forEach((note) => {
          const noteMeasureIds = note?.measures?.map((m) => m.measureId);
          if (measureId && noteMeasureIds?.includes(measureId)) {
            itemNotes.push(note);
          }
        });
        return { ...column, notes: itemNotes, hasNotes: itemNotes.length > 0 };
      }),
    );
  };

  const toggleGridTab = (id: number) => {
    setSelectedTab(id);
  };

  const onExcelExport = () => {
    if (exportRef && exportRef.current) {
      const workbook = exportRef.current.workbookOptions();
      const rows = workbook.sheets[0].rows;

      const objectRow = {
        cells: [
          { value: selectedObject?.name },
        ],
      };

      const periodRow = {
        cells: [
          { value: selectedPeriod?.name },
        ],
      };

      const currencyRow = {
        cells: [
          { value: localCurrency?.currency },
        ],
      };
      // let groupingLevel = gridSetup?.singlePeriod ? grouping?.length || 0 : 0; //workbook.sheets[0].columns.length - gridColumns.length - 1;
      // let colOffset = (localCurrency?.currency === '_' && gridSetup?.singlePeriod ? 2 : 1) + groupingLevel;

      let groupingDepth = gridSetup?.singlePeriod ? grouping?.length || 0 : 0; //workbook.sheets[0].columns.length - gridColumns.length - 1;
      let colOffset = (localCurrency?.currency === '_' && gridSetup?.singlePeriod ? 2 : 1) + groupingDepth;

      let groupLevel = 0;

      rows?.forEach((row: any) => {
        if (row.type === 'data' || row.type === 'group-footer' || row.type === 'footer') {
          row.cells.forEach((cell: any, index: number) => {
            let col = gridColumns[index - colOffset];

            const measure = boardData.measures?.find((m) => m.id === col?.measureId);
            const property = boardData.staticProperties?.find((p) => p.id === col?.templatePropertyId);

            if (cell.value && col?.type === 'measure') {
              cell.format = measure?.isPercent
                ? excelFormat.percentFormat(col?.scale || measure?.scale || 0)
                : excelFormat.numberFormat(col?.scale || measure?.scale || 0);
            }
            if (cell.value && col?.type === 'property') {
              if (property && property.dataType === 'date') {
                const parsedDate = new Date(cell.value);
                cell.value = intl.formatDate(parsedDate, 'd');
              }
              if (property && property.dataType === 'number') {
                cell.format = excelFormat.numberFormat(col?.scale || property?.scale || 0);
              }
              if (property && property.dataType === 'percent') {
                cell.format = excelFormat.percentFormat(col?.scale || property?.scale || 0);
              }
              if (row.type === 'group-footer' || row.type === 'footer')
                cell.bold = true;
            }
          });
        }

        if (row.type === 'group-header') {
          groupLevel++;
          row.cells.forEach((cell) => {
            if (typeof cell.value === 'string') {
              const groupHeaderParts = cell.value.split(':');
              if (groupHeaderParts.length > 1) {
                cell.value = groupHeaderParts.slice(1).join(':').trim();
              }
            }
          });
        }
        if (row.type === 'group-footer') {
          if (grouping?.length > 0 && groupLevel > 0 && grouping.length >= groupLevel && !grouping[groupLevel - 1].showAggregates) {
            row.cells.forEach((cell: any) => {
              cell.value = undefined;
            });
          }
          groupLevel--;
        }
      });

      let rowSplit = 4;
      if (gridSetup?.singlePeriod) {
        rows?.unshift(objectRow, periodRow, currencyRow);
      } else {
        rows?.unshift(objectRow, currencyRow);
        rowSplit = 3;
      }
      let freeze = workbook.sheets[0].freezePane || {};
      workbook.sheets[0].freezePane = { ...freeze, rowSplit: rowSplit };

      exportRef.current.save(workbook);

      // if (gridSetup?.singlePeriod) {
      //   rows?.unshift(objectRow, periodRow, currencyRow);
      // } else {
      //   rows?.unshift(objectRow, currencyRow);
      // }
      //
      // exportRef.current.save(workbook);
    }
  };

  const sortCompare = (itemA: any, itemB: any, field: string, dir: string) => {
    let adjust = dir === 'asc' ? 1 : -1;
    let result = 0;
    if (itemA[field].rowAmount < itemB[field].rowAmount) result = -1;
    if (itemA[field].rowAmount > itemB[field].rowAmount) result = 1;
    return result * adjust;
  };

  const sortDescriptors = (sort: SortDescriptor[]) => {
    return sort.map((item) => {
      if (item.field.startsWith('ID')) {
        let colId = item.field.substring(2, item.field.indexOf('_'));

        let column = gridColumns.find((col) => col.columnId === colId);
        if (column && column.type === 'measure') {
          return {
            ...item,
            compare: (itemA: any, itemB: any) => sortCompare(itemA, itemB, item.field, item.dir || 'asc'),
          };
        }
      }
      return item;
    });
  };

  useEffect(() => {
    let isMobile = window.innerWidth;
    if (isMobile < 960) {
      setIsMobile(true);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    let wSetup = widgetSetup(widget);

    if (wSetup) {
      setGridSetup(wSetup);
      setIsHistorical(!wSetup.singlePeriod);
      extractGridTabs(wSetup);
    }

    setLocalCurrency(selectedCurrency);

    if (selectedObject && selectedPeriod && selectedCurrency) {
      const { nodes, groups } = defineNodes(widget, selectedObject);
      generateColumns(wSetup, nodes);
      setNodes(nodes);
      setNodeGroups(groups);

      if (scenarios) {
        setWidgetCurrencies(generateCurrencies(widget, nodes, scenarios));
      }

      const drill = wSetup.sliceGroup && wSetup.sliceGroupLevel;
      const prom = drill
        ? fetchNodeData({
          ...widget,
          investorObjectId: widget.investorObjectId || parseInt(params.investorId),
        }, nodes, measuresForFetch(wSetup), selectedPeriod, selectedCurrency.currency || '', false).then((data) => {
          return {
            measureValues: data,
            staticValues: [],
          };
        })
        : fetchCombinedData(
          { ...widget, investorObjectId: widget.investorObjectId || parseInt(params.investorId) },
          nodes,
          groups,
          measuresForFetch(wSetup),
          propertiesForFetch(wSetup),
          selectedPeriod,
          selectedCurrency.currency || '',
          false,
        );

      prom
        .then((res) => {
          let data = wSetup.singlePeriod
            ? prepareData(wSetup, res?.measureValues, nodes, res?.staticValues)
            : prepareDataHistorical(wSetup, res?.measureValues, nodes, res?.staticValues);

          //let transformedData = transformDateRowAmount(data);
          //setGridData(transformedData);
          setGridData(data);

          setLoading(false);
        })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  }, [selectedObject, selectedPeriod, selectedCurrency, selectedTab]);

  useEffect(() => {
    if (notes) {
      filterNotes(notes);
    }
  }, [notes, widget, gridSetup, selectedTab]);

  useEffect(() => {
    let widgetSetup = JSON.parse(widget.setup);

    if (widgetSetup.definitions[selectedTab].filter) {
      let currentDefinitionFilters = widgetSetup.definitions[selectedTab].filter;
      let currentDefinitionSorting = widgetSetup.definitions[selectedTab].sorting;
      let currentDefinitionGrouping = widgetSetup.definitions[selectedTab].grouping;


      if (gridColumns.length > 0) {
        const updateFilters = (filters) =>
          filters.map((filter) => {
            if (filter.field) {
              const matchingColumn = gridColumns.find((el) => el.columnId === filter.columnId);
              const updatedField = matchingColumn !== undefined ? getFieldName(matchingColumn, isHistorical) : filter.field;

              return { ...filter, field: updatedField };
            } else if (filter.filters) {
              return { ...filter, filters: updateFilters(filter.filters) };
            }
            return { ...filter };
          });

        const updatedFilters = {
          ...currentDefinitionFilters,
          filters: updateFilters(currentDefinitionFilters.filters),
        };

        const updatedSortingRules = currentDefinitionSorting && currentDefinitionSorting.length !==0
          ? currentDefinitionSorting.map((s) => {
            const columnForSort = gridColumns.find((el) => el.columnId === s.field.columnId);
            return {
              field: getFieldName(columnForSort, isHistorical),
              dir: s.direction.value,
            };
          })
          : initialSortRuleSingle;

        if (currentDefinitionGrouping && !isHistorical) {
          const totals = gridColumns.filter(x => x.total)
            .map(x => ({field: getFieldName(x, false), aggregate: 'sum'}));

          const updatedGrouping = currentDefinitionGrouping.map((group) => {
            const columnForGrouping = gridColumns.find((el) => el.columnId === group.field.columnId);
            return {
              field: getFieldName(columnForGrouping, isHistorical),
              hideColumn: group.hideColumn,
              dir: group?.dir?.value,
              aggregates: group.showAggregates ? totals : [],
              showAggregates: group.showAggregates,
            };
          });
          setGrouping(updatedGrouping);
        }

        setFilter(updatedFilters);
        setSortRules(updatedSortingRules);

      }
    }
  }, [selectedTab, gridColumns]);


  return window.innerWidth < 960 && gridSetup?.hideOnMobile ? null : (
    <WidgetWrapper
      widget={widget}
      localCurrency={localCurrency}
      widgetCurrencies={widgetCurrencies}
      handleLocalCurrencyChange={handleLocalCurrencyChange}
      notes={gridNotes}
      moveWidget={moveWidget}
      removeWidget={removeWidget}
      selectedObject={selectedObject}
      customisation={customisation}
      handleLink={handleLink}
      content={content}
      homeContent={homeContent}
      widgetView={widgetView}
      handleUnpinFromBoardCallback={handleUnpinWidget}
      handlePinCallback={handlePinWidget}
      handleUnpin={handleUnpinTrigger}
      unpinId={unpinId}
      renderIcons={<Icon onClick={onExcelExport} name='far fa-file-excel' className='widget-icon pointer' />}>
      {loading ? (
        <ContentLoading />
      ) : (
        <div className='grid-widget-wrapper'>
          {gridTabs.length > 1 && <Tabs localTabId={selectedTab} tabsArray={gridTabs} onClick={toggleGridTab} />}
          {gridSetup?.singlePeriod && (
            <SinglePeriodGrid
              ref={exportRef}
              gridData={gridData}
              filter={filter}
              sort={sortRules}
              grouping={grouping}
              widget={widget}
              boardData={boardData}
              isMobile={isMobile}
              gridColumns={gridColumns}
              boards={boards}
              selectedCurrency={localCurrency}
              gridClassName={`${gridTabs.length > 1 ? 'k-grid-with-tabs' : 'k-grid-without-tabs'}`}
              openCashflowDialog={openCashflowDialog}
              setup={widgetSetup(widget)}
              sortDescriptors={sortDescriptors}
            />
          )}
          {!gridSetup?.singlePeriod && (
            <MultiPeriodGrid
              ref={exportRef}
              gridData={gridData}
              filter={filter}
              widget={widget}
              boardData={boardData}
              boards={boards}
              selectedCurrency={localCurrency}
              isMobile={isMobile}
              gridColumns={gridColumns}
              gridClassName={`${gridTabs.length > 1 ? 'k-grid-with-tabs' : 'k-grid-without-tabs'}`}
              openCashflowDialog={openCashflowDialog}
              setup={widgetSetup(widget)}
              sortDescriptors={sortDescriptors}
            />
          )}
        </div>
      )}
    </WidgetWrapper>
  );
};

export default GridWidget;
